import React, { useState, useEffect } from 'react';

// Extended interface to include all asset details for the card
interface GiveawayAsset {
  id: number;
  name: string;
  description: string | null;
  price: number;
  category: string | null;
  reward_id: string;
  image_path: string | null;
  status: 'claimed' | 'issuedtomarketors' | 'notclaimed';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  assets: GiveawayAsset[];
  onSetDefault: (rewardId: string) => void;
  currentDefaultId: string | null;
}

const SetDefaultRewardModal: React.FC<Props> = ({ isOpen, onClose, assets, onSetDefault, currentDefaultId }) => {
  const [previewAsset, setPreviewAsset] = useState<GiveawayAsset | null>(null);

  useEffect(() => {
    if (isOpen) {
      const assetToPreview = assets.find(a => a.reward_id === currentDefaultId) || null;
      setPreviewAsset(assetToPreview);
    }
  }, [isOpen, currentDefaultId, assets]);

  const handleSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRewardId = e.target.value;
    onSetDefault(selectedRewardId);
    const assetToPreview = assets.find(a => a.reward_id === selectedRewardId) || null;
    setPreviewAsset(assetToPreview);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50" onClick={onClose}>
      <div className="gothic-modal-container w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center">
            <h2 className="gothic-modal-title">Set Default Reward</h2>
            <button onClick={onClose} className="gothic-button gothic-button-secondary">Close</button>
        </div>
        <div className="my-4">
            <p className="mb-2 text-gray-400">Select an asset to be the default reward for new winners when one isn't specified.</p>
            <select
                value={currentDefaultId || ''}
                onChange={handleSelectionChange}
                className="gothic-input w-full"
            >
                <option value="" disabled>Select a reward</option>
                {assets.map(asset => (
                <option key={asset.id} value={asset.reward_id}>
                    {asset.name} ({asset.reward_id})
                </option>
                ))}
            </select>
        </div>
        
        {/* Preview Card */}
        {previewAsset && (
            <div className="mt-6">
                <h3 className="text-xl font-semibold mb-2 text-center gothic-text-gold">Preview</h3>
                <div className="nft-card is-default mx-auto">
                    <div className="nft-card-image-container">
                        <img src={previewAsset.image_path ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/files/${previewAsset.image_path}` : '/assets/img/default-asset.png'} alt={previewAsset.name} className="nft-card-image" />
                    </div>
                    <div className="nft-card-content">
                        <h3 className="nft-card-title">{previewAsset.name}</h3>
                        <div className="nft-card-reward-id" title={previewAsset.reward_id}>
                        <span>ID:</span> {previewAsset.reward_id}
                        </div>
                        
                        <div className="nft-card-properties">
                            <div className="property"><span className="prop-name">Price:</span> <span className="prop-value">${previewAsset.price}</span></div>
                            <div className="property"><span className="prop-name">Category:</span> <span className="prop-value">{previewAsset.category || 'N/A'}</span></div>
                        </div>

                        <p className="nft-card-description">{previewAsset.description || 'No description available.'}</p>

                        <div className="nft-card-footer">
                            <button disabled className="nft-card-explorer-link">
                            View on AVAX Explorer
                        </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default SetDefaultRewardModal;
